import { CommonModule } from '@angular/common'
import { Component, ElementRef, OnInit, ViewChild } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { ActivatedRoute, Router, RouterLink } from '@angular/router'

import { BaseComponent } from '../../shared'
import { PurchasingDocument, PurchasingInvoice, PurchasingInvoiceLineItem, PurchasingOrder } from '../../shared/data-models'
import { FeatureType } from '../../shared/enums'

interface PoOption {
    id: string
    poNumber: string
    vendorName: string
    job: string
    equipment: string
    workOrder: string
    status: string
}

@Component({
    selector: 'app-purchasing-invoicing',
    templateUrl: './purchasing-invoicing.component.html',
    styleUrls: ['./purchasing-invoicing.component.css'],
    standalone: true,
    imports: [CommonModule, FormsModule, RouterLink],
})
export class PurchasingInvoicingComponent extends BaseComponent implements OnInit {
    @ViewChild('invoiceInput') invoiceInput!: ElementRef<HTMLInputElement>
    @ViewChild('docInput') docInput!: ElementRef<HTMLInputElement>

    public invoices: PurchasingInvoice[] = []
    public filteredInvoices: PurchasingInvoice[] = []
    public selectedInvoice: PurchasingInvoice | null = null

    public openOrders: PoOption[] = []
    public selectedOrderId = ''
    public showUnmatchedOnly = false

    public editing = false
    public creating = false
    public editModel: PurchasingInvoice | null = null

    public isProcessingAi = false
    public isSaving = false

    public readonly statuses = ['PendingReview', 'Approved', 'OnHold', 'Rejected']

    public get canEdit(): boolean {
        return this.services.authentication.hasFeature([FeatureType.PurchasingInvoicingEdit])
    }

    constructor(
        private route: ActivatedRoute,
        private router: Router
    ) {
        super()
        this.isInitialLoading = false
    }

    ngOnInit(): void {
        this.loadInvoices()
        this.loadOpenOrders()

        this.route.queryParamMap.subscribe(params => {
            const orderId = params.get('orderId')
            if (orderId) {
                this.startInvoiceForOrder(orderId)
            }
        })
    }

    private loadInvoices(): void {
        this.services.data.http.get<PurchasingInvoice[]>('/api/PurchasingInvoice').subscribe({
            next: result => {
                this.invoices = result || []
                this.applyFilter()
            },
            error: () => this.services.notification.error('Failed to load invoices'),
        })
    }

    private loadOpenOrders(): void {
        this.services.data.http.get<PurchasingOrder[]>('/api/PurchasingOrder').subscribe({
            next: result => {
                this.openOrders = (result || [])
                    .filter(o => !['Closed', 'Cancelled'].includes(o.status))
                    .map(o => ({
                        id: o.id,
                        poNumber: o.poNumber,
                        vendorName: o.vendorName,
                        job: o.job,
                        equipment: o.equipment,
                        workOrder: o.workOrder,
                        status: o.status,
                    }))
            },
            error: () => {},
        })
    }

    public applyFilter(): void {
        this.filteredInvoices = this.showUnmatchedOnly
            ? this.invoices.filter(i => !i.isMatched && !i.purchaseOrderId)
            : this.invoices
    }

    public toggleUnmatchedFilter(): void {
        this.showUnmatchedOnly = !this.showUnmatchedOnly
        this.applyFilter()
    }

    public selectInvoice(invoice: PurchasingInvoice): void {
        this.editing = false
        this.creating = false
        this.editModel = null
        this.services.data.http.get<PurchasingInvoice>(`/api/PurchasingInvoice?id=${invoice.id}`).subscribe({
            next: result => (this.selectedInvoice = result),
            error: () => this.services.notification.error('Failed to load invoice'),
        })
    }

    public startNew(): void {
        this.selectedInvoice = null
        this.creating = true
        this.editing = true
        this.selectedOrderId = ''
        this.editModel = {
            id: '00000000-0000-0000-0000-000000000000',
            status: 'PendingReview',
            source: 'Manual',
            lineItems: [],
            documents: [],
        } as unknown as PurchasingInvoice
    }

    public startInvoiceForOrder(orderId: string): void {
        this.startNew()
        this.selectedOrderId = orderId
        this.onOrderSelected()
    }

    public onOrderSelected(): void {
        if (!this.editModel) return

        if (!this.selectedOrderId) {
            this.editModel.purchaseOrderId = undefined
            this.editModel.poNumber = ''
            this.editModel.job = ''
            this.editModel.equipment = ''
            this.editModel.workOrder = ''
            return
        }

        const order = this.openOrders.find(o => o.id === this.selectedOrderId)
        this.editModel.purchaseOrderId = this.selectedOrderId
        this.editModel.poNumber = order?.poNumber || ''
        this.editModel.vendorName = order?.vendorName || this.editModel.vendorName
        this.editModel.job = order?.job || ''
        this.editModel.equipment = order?.equipment || ''
        this.editModel.workOrder = order?.workOrder || ''

        this.services.data.http
            .get<PurchasingInvoiceLineItem[]>(`/api/PurchasingInvoice/GetInvoiceableLines?orderId=${this.selectedOrderId}`)
            .subscribe({
                next: lines => {
                    if (!this.editModel) return
                    this.editModel.lineItems = (lines || []).map(l => ({
                        ...l,
                        id: '00000000-0000-0000-0000-000000000000',
                        quantity: undefined,
                        lineTotal: undefined,
                    })) as PurchasingInvoiceLineItem[]
                },
                error: () => this.services.notification.error('Failed to load invoiceable lines'),
            })
    }

    public linkPoToSelected(): void {
        if (!this.selectedInvoice || !this.selectedOrderId) return
        this.editing = true
        this.creating = false
        this.editModel = JSON.parse(JSON.stringify(this.selectedInvoice))
        this.editModel!.purchaseOrderId = this.selectedOrderId
        this.onOrderSelected()
    }

    public startEdit(): void {
        if (!this.selectedInvoice) return
        this.creating = false
        this.editing = true
        this.selectedOrderId = this.selectedInvoice.purchaseOrderId || ''
        this.editModel = JSON.parse(JSON.stringify(this.selectedInvoice))
    }

    public cancelEdit(): void {
        this.editing = false
        this.creating = false
        this.editModel = null
        this.router.navigate([], { relativeTo: this.route, queryParams: {} })
    }

    public save(): void {
        if (!this.editModel) return

        const linesToSave = (this.editModel.lineItems || []).filter(l => (l.quantity ?? 0) > 0)
        if (linesToSave.length === 0 && !this.editModel.total) {
            this.services.notification.error('Enter at least one line quantity or a total amount')
            return
        }

        const payload = {
            ...this.editModel,
            lineItems: linesToSave,
            purchaseOrderId: this.editModel.purchaseOrderId || null,
        }
        this.isSaving = true

        const request = this.creating
            ? this.services.data.http.post<PurchasingInvoice>('/api/PurchasingInvoice', payload)
            : this.services.data.http.put<PurchasingInvoice>('/api/PurchasingInvoice', payload)

        request.subscribe({
            next: result => {
                this.isSaving = false
                this.services.notification.success('Invoice saved')
                this.editing = false
                this.creating = false
                this.editModel = null
                this.loadInvoices()
                this.router.navigate([], { relativeTo: this.route, queryParams: {} })
                this.selectInvoice(result)
            },
            error: () => {
                this.isSaving = false
                this.services.notification.error('Failed to save invoice')
            },
        })
    }

    public deleteInvoice(invoice: PurchasingInvoice): void {
        if (!confirm(`Archive invoice ${invoice.invoiceNumber || ''}?`)) return
        this.services.data.http.delete(`/api/PurchasingInvoice/${invoice.id}`).subscribe({
            next: () => {
                this.services.notification.success('Invoice archived')
                if (this.selectedInvoice?.id === invoice.id) this.selectedInvoice = null
                this.loadInvoices()
            },
            error: () => this.services.notification.error('Failed to archive invoice'),
        })
    }

    public triggerInvoiceScan(): void {
        this.invoiceInput.nativeElement.click()
    }

    public onInvoiceFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement
        if (!input.files || input.files.length === 0) return
        const file = input.files[0]
        input.value = ''

        this.isProcessingAi = true
        const formData = new FormData()
        formData.append('file', file, file.name)

        const orderId = this.selectedOrderId || this.editModel?.purchaseOrderId
        const url = orderId
            ? `/api/PurchasingInvoice/ExtractFromInvoice?orderId=${orderId}`
            : '/api/PurchasingInvoice/ExtractFromInvoice'

        this.services.data.http.post<PurchasingInvoice>(url, formData).subscribe({
            next: result => {
                this.isProcessingAi = false
                this.editing = true
                this.creating = true
                this.selectedOrderId = result.purchaseOrderId || ''
                this.editModel = result
                if (!this.editModel.lineItems) this.editModel.lineItems = []
                if (!this.editModel.documents) this.editModel.documents = []
                const matchMsg = result.isMatched || result.purchaseOrderId
                    ? 'Extracted invoice — matched to PO, review and save'
                    : 'Extracted invoice — no PO match, review and save'
                this.services.notification.success(matchMsg)
            },
            error: err => {
                this.isProcessingAi = false
                this.services.notification.error(err.error?.error || 'Failed to extract invoice')
            },
        })
    }

    public triggerDocUpload(): void {
        this.docInput.nativeElement.click()
    }

    public onDocFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement
        const target = this.selectedInvoice
        if (!input.files || input.files.length === 0 || !target) return

        const formData = new FormData()
        formData.append('id', target.id)
        formData.append('documentType', 'Invoice')
        for (let i = 0; i < input.files.length; i++) {
            formData.append('files', input.files[i], input.files[i].name)
        }
        input.value = ''

        this.services.data.http.post<PurchasingDocument[]>('/api/PurchasingInvoice/UploadDocuments', formData).subscribe({
            next: result => {
                if (this.selectedInvoice) this.selectedInvoice.documents = result || []
                this.services.notification.success('Document uploaded')
            },
            error: () => this.services.notification.error('Failed to upload document'),
        })
    }

    public addLine(): void {
        if (!this.editModel) return
        if (!this.editModel.lineItems) this.editModel.lineItems = []
        this.editModel.lineItems.push({
            id: '00000000-0000-0000-0000-000000000000',
            description: '',
            quantity: undefined,
            unitPrice: undefined,
            lineTotal: undefined,
        } as PurchasingInvoiceLineItem)
    }

    public removeLine(index: number): void {
        if (!this.editModel?.lineItems) return
        this.editModel.lineItems.splice(index, 1)
    }

    public updateLineTotal(line: PurchasingInvoiceLineItem): void {
        const qty = line.quantity ?? 0
        const price = line.unitPrice ?? 0
        if (qty > 0 && price > 0) {
            line.lineTotal = qty * price
        }
    }

    public lineInvoiceClass(line: PurchasingInvoiceLineItem): string {
        const ordered = line.quantityOrdered ?? 0
        const invoicedToDate = line.quantityInvoicedToDate ?? 0
        const invoiceNow = line.quantity ?? 0
        const cumulative = invoicedToDate + invoiceNow

        if (invoiceNow <= 0) return ''
        if (ordered > 0 && cumulative > ordered) return 'table-danger'
        if (ordered > 0 && cumulative === ordered) return 'table-success'
        return 'table-warning'
    }

    public statusBadgeClass(status: string): string {
        switch ((status || '').toLowerCase()) {
            case 'pendingreview':
                return 'bg-warning text-dark'
            case 'approved':
                return 'bg-success'
            case 'onhold':
                return 'bg-danger'
            case 'rejected':
                return 'bg-danger'
            default:
                return 'bg-secondary'
        }
    }

    public sourceBadgeClass(source: string): string {
        switch ((source || '').toLowerCase()) {
            case 'email':
                return 'bg-info text-dark'
            case 'ai':
                return 'bg-primary'
            default:
                return 'bg-secondary'
        }
    }

    public documentUrl(doc: PurchasingDocument): string {
        return `/Resources/Images/${doc.url}`
    }
}
